import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { StaffInfo } from '../lib/types'
import { setAuthToken } from '../lib/api-client'

interface AuthStore {
  isAuthenticated: boolean
  token: string | null
  staff: StaffInfo | null
  loading: boolean
  error: string | null

  // Actions
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  clearError: () => void
  refreshToken: () => Promise<void>
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      token: null,
      staff: null,
      loading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ loading: true, error: null })
        try {
          const apiUrl = ''
          const response = await fetch(`${apiUrl}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          })

          const data = await response.json()

          if (data.success && data.data) {
            set({
              isAuthenticated: true,
              token: data.data.token,
              staff: data.data.staff,
              loading: false,
              error: null,
            })
            // Set auth token for API client
            setAuthToken(data.data.token)
            return true
          } else {
            set({
              error: data.error || 'Login failed',
              loading: false,
            })
            return false
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Network error',
            loading: false,
          })
          return false
        }
      },

      logout: () => {
        set({
          isAuthenticated: false,
          token: null,
          staff: null,
          error: null,
        })
        // Clear auth token from API client
        setAuthToken(null)
      },

      clearError: () => set({ error: null }),

      refreshToken: async () => {
        const { token } = get()
        if (!token) return

        try {
          const apiUrl = ''
          const response = await fetch(`${apiUrl}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })

          const data = await response.json()

          if (!data.success) {
            // Token is invalid, logout
            get().logout()
          }
        } catch {
          // Error verifying token, logout
          get().logout()
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        staff: state.staff,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // When loading from persist, set the auth token on API client
        if (state?.token) {
          setAuthToken(state.token)
        }
      },
    }
  )
)
